import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { colores, fuentes } from '../../styles/variablesCss';

const CheckoutSteps = ({ currentStep }) => {
  // Estado para controlar responsividad
  const [windowWidth, setWindowWidth] = useState(
    Platform.OS === 'web' && typeof window !== 'undefined' ? window.innerWidth : 768
  );
  const isMobile = windowWidth < 768;

  // Listener para cambios de tamaño de ventana
  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const handleResize = () => {
        setWindowWidth(window.innerWidth);
      };
      
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  // Definir los pasos del checkout
  const steps = [
    { id: 'resumen', title: 'Resumen del pedido', icon: 'cart-outline' },
    { id: 'formaPago', title: 'Método de entrega', icon: 'truck-delivery-outline' },
    { id: 'horarioRetiro', title: 'Horario de retiro', icon: 'clock-outline' },
    { id: 'facturacion', title: 'Datos de facturación', icon: 'file-document-outline' },
    { id: 'metodoPago', title: 'Forma de pago', icon: 'credit-card-outline' }
  ];

  // Encontrar el índice del paso actual
  const currentIndex = steps.findIndex(step => step.id === currentStep);

  // Estilos dinámicos
  const dynamicStyles = {
    container: {
      ...styles.container,
      padding: isMobile ? 5 : 10,
      paddingVertical: isMobile ? 5 : 15,
      width: isMobile ? '95%' : 'auto',
      flex: 1,
    },
    stepsContainer: {
      ...styles.stepsContainer,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: isMobile ? 'space-around' : 'space-between',
      flexWrap: 'nowrap',
    },
    stepItem: {
      ...styles.stepItem,
      width: 'auto',
      marginHorizontal: isMobile ? 3 : 5,
      flexShrink: 1,
    },
    stepIcon: {
      ...styles.stepIcon,
      width: isMobile ? 32 : 24,
      height: isMobile ? 32 : 24,
      borderRadius: isMobile ? 16 : 12,
    },
  };

  return (
    <View style={dynamicStyles.container}>
      <View style={dynamicStyles.stepsContainer}>
        {steps.map((step, index) => {
          // Determinar el estado del paso: completado, actual o pendiente
          const isCompleted = index < currentIndex;
          const isCurrent = index === currentIndex;
          const isPending = index > currentIndex;
          
          // Determinar estilos para este paso
          const iconStyle = [
            dynamicStyles.stepIcon,
            isCompleted ? styles.completedIcon : isCurrent ? styles.currentIcon : styles.pendingIcon
          ];
          
          return (
            <View key={step.id} style={dynamicStyles.stepItem}>
              {isMobile ? (
                // Solo icono en móvil - no clicable
                <View style={iconStyle}>
                  <MaterialCommunityIcons 
                    name={isCompleted ? 'check' : step.icon} 
                    size={isMobile ? 20 : 16} 
                    color={isCompleted || isCurrent ? colores.blanco : colores.grisClaro} 
                  />
                </View>
              ) : (
                // Icono con texto en desktop - no clicable
                <View style={styles.iconWithTextContainer}>
                  <View style={iconStyle}>
                    <MaterialCommunityIcons 
                      name={isCompleted ? 'check' : step.icon} 
                      size={16} 
                      color={isCompleted || isCurrent ? colores.blanco : colores.grisClaro} 
                    />
                  </View>
                  
                  <Text style={[
                    styles.stepText,
                    isCurrent ? styles.currentStepText : 
                    isCompleted ? styles.completedStepText : styles.pendingStepText
                  ]} numberOfLines={1}>
                    {step.title}
                  </Text>
                  
                  {isCurrent && (
                    <MaterialCommunityIcons 
                      name="chevron-right" 
                      size={14} 
                      color={colores.rojo} 
                      style={styles.arrowIcon}
                    />
                  )}
                </View>
              )}
            </View>
          );
        })}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: colores.fondoClaro,
    padding: 10,
    borderRadius: 8,
    alignSelf: 'center',
    maxWidth: 1100,
  },
  stepsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  stepItem: {
    marginHorizontal: 10,
    marginBottom: 10,
    flexBasis: 'auto',
  },
  iconWithTextContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  stepIcon: {
    width: 24,
    height: 24,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 8,
  },
  completedIcon: {
    backgroundColor: colores.verde,
  },
  currentIcon: {
    backgroundColor: colores.rojo,
  },
  pendingIcon: {
    backgroundColor: colores.fondoClaro,
    borderWidth: 1,
    borderColor: colores.grisClaro,
  },
  stepText: {
    fontSize: 13,
    paddingHorizontal: 2,
  },
  currentStepText: {
    color: colores.rojo,
    fontFamily: fuentes.bold,
    fontSize: 16,
  },
  completedStepText: {
    color: colores.verde,
    fontFamily: fuentes.semiBold,
  },
  pendingStepText: {
    color: colores.grisClaro,
    fontFamily: fuentes.regular,
    fontSize: 16,
  },
  arrowIcon: {
    marginLeft: 5,
  },
});

export default CheckoutSteps;
